import React, { useState, useEffect } from "react";
import { Container, Row, Col } from "../Grid";
import useMediaQuery from "hooks/useMediaQuery";
import { BreakPoints } from "utils/BreakPoints";
import Circle from "./Circle";
import styled from "styled-components";
import { BodyCallCta } from "components/Partials";

interface blockProps {
  readonly isAutism?: boolean;
  readonly subTitleColor?: string;
  readonly paragraphColor?: string;
  readonly useCta?: boolean;
  readonly alignment?: string;
  readonly moreMargin?: boolean;
  readonly shorter?: boolean;
}
const RowPositionAbsolute = styled(Row)`
  position: absolute;
  left: 0;
`;
const FullWidthContainer = styled(Container)`
  width: 100%;
  position: relative;
  margin: 0;
  max-width: 100%;
`;
const EachBlock = styled.div<blockProps>`
  height: 500px;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;

  @media (max-width: 768px) {
    justify-content: ${(props) => props.shorter && "flex-start"};
    padding-top: ${(props) => props.shorter && "27px"};
    height: ${(props) => (props.shorter ? "222px" : "290px")};
  }
`;

const TitleBlock = styled.div<blockProps>`
  width: 100%;
  margin-left: 36px;
  margin-right: ${(props) => (props.alignment !== "left" ? "60px" : "0px")};
  padding-left: ${(props) =>
    props.alignment === "left" && props.isAutism && "60px"};
  padding-right: ${(props) =>
    props.alignment === "right" && props.isAutism && "60px"};
  max-width: ${(props) => (props.alignment === "left" ? "551px" : "530px")};

  h3 {
    font-style: normal;
    font-weight: 600;
    font-size: 40px;
    line-height: 100%;
    letter-spacing: -0.01em;
    margin-bottom: ${(props) => (props.moreMargin ? "41px" : "16px")};
    color: ${(props) => props.subTitleColor};
  }

  p {
    font-style: normal;
    font-weight: 500;
    font-size: 20px;
    line-height: 140%;
    letter-spacing: -0.01em;
    color: ${(props) =>
      props.paragraphColor ? props.paragraphColor : props.subTitleColor};
  }

  li {
    color: ${(props) =>
      props.paragraphColor ? props.paragraphColor : props.subTitleColor};
    margin-left: 2rem;
  }

  @media (max-width: 1100px) {
    margin-right: ${(props) => (props.alignment !== "left" ? "30px" : "0px")};
  }

  @media (max-width: 1024px) {
    margin-right: ${(props) => (props.alignment !== "left" ? "15px" : "0px")};

    h3 {
      font-style: normal;
      font-weight: 600;
      font-size: 28.3926px;
      line-height: 100%;
      letter-spacing: -0.01em;
      margin-bottom: ${(props) => (props.moreMargin ? "29px" : "13px")};
    }

    p {
      font-style: normal;
      font-weight: 500;
      font-size: 14.2px;
      line-height: 140%;
      letter-spacing: -0.01em;
      max-width: 438px;
    }
  }

  @media (max-width: 768px) {
    width: auto;
    margin-left: 0px;

    padding-left: ${(props) =>
      props.alignment === "left" && props.isAutism && "0px"};
    padding-right: ${(props) =>
      props.alignment === "right" && props.isAutism && "0px"};

    h3 {
      font-size: 24px;
      line-height: 110%;
      margin-bottom: ${(props) => (props.moreMargin ? "28px" : "12px")};
    }

    p {
      font-size: 16px;
      line-height: 140%;
      max-width: 342px;
    }
  }
`;
const BodyCallCtaWrapper = styled.div`
  display: flex;
  justify-content: start;
  margin-top: 12px;
  @media (max-width: 768px) {
    justify-content: center;
  }
`;

interface Props {
  alignment?: string;
  title?: string;
  content?: string;
  image?: string;
  icon?: string;
  backgroundColor?: string;
  circleColor?: string;
  isAutism?: boolean;
  marginBottom?: boolean;
  useCta?: boolean;
  ctaText?: string;
  ctaPhoneText?: string;
  ctaPhoneLink?: string;
  ctaBackground?: string;
  ctaSeparator?: string;
  website?: string;
  subTitleColor?: string;
  paragraphColor?: string;
  moreMargin?: boolean;
  shorter?: boolean;
}

const Block: React.FC<Props> = ({
  alignment,
  title,
  content,
  image,
  icon,
  backgroundColor,
  circleColor,
  isAutism,
  marginBottom = false,
  useCta = false,
  ctaText = "",
  ctaPhoneText = "",
  ctaPhoneLink = "",
  ctaBackground = "",
  ctaSeparator = "",
  website = "",
  subTitleColor,
  paragraphColor,
  moreMargin,
  shorter,
}) => {
  const isTabletBreakPoint = useMediaQuery(BreakPoints.MOBILE);
  const [isTablet, setTablet] = useState(false);

  useEffect(() => {
    if (isTabletBreakPoint) {
      setTablet(true);
    } else {
      setTablet(false);
    }
  }, [isTabletBreakPoint]);

  return (
    <>
      {!isTablet ? (
        <FullWidthContainer
          className="container-full-width"
          style={{ marginBottom: marginBottom && !isAutism ? "24px" : 0 }}
        >
          <Row
            className="rowwith-background-image"
            style={{
              display: "flex",
              flexDirection: alignment === "left" ? "row" : "row-reverse",
            }}
          >
            <Col size={12}>
              <EachBlock
                style={{
                  backgroundImage: `url(${image})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                }}
              />
            </Col>
            <Col size={12}>
              <EachBlock style={{ backgroundColor: backgroundColor }} />
            </Col>
          </Row>
          <Container
            style={{
              position: "absolute",
              zIndex: 1,
              top: 0,
              left: "50%",
              transform: "translateX(-50%)",
            }}
          >
            <Row
              className="rowwith-content"
              style={{
                marginBottom: "24px",
                display: "flex",
                flexDirection: alignment === "left" ? "row-reverse" : "row",
              }}
            >
              <Col size={6}>
                <EachBlock shorter={shorter}>
                  <TitleBlock
                    isAutism={isAutism}
                    subTitleColor={subTitleColor}
                    paragraphColor={paragraphColor}
                    alignment={alignment}
                    moreMargin={moreMargin}
                    shorter={shorter}
                  >
                    {/* <Circle icon={icon} circleColor={circleColor} /> */}
                    <h3>{title}</h3>
                    <div dangerouslySetInnerHTML={{ __html: content }}></div>
                    {useCta && (
                      <BodyCallCtaWrapper>
                        <BodyCallCta
                          backgroundColor={ctaBackground}
                          separatorColor={ctaSeparator}
                          text={ctaText}
                          phoneText={ctaPhoneText}
                          ctaPhoneLink={ctaPhoneLink}
                          component="TreatmentBlock"
                          website={website}
                        />
                      </BodyCallCtaWrapper>
                    )}
                  </TitleBlock>
                </EachBlock>
              </Col>
              <Col size={6}>
                {/* <EachBlock
                  style={{
                    backgroundImage: `url(${image})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                  }}
                /> */}
              </Col>
            </Row>
          </Container>
        </FullWidthContainer>
      ) : (
        <FullWidthContainer>
          <Container>
            <Row
              style={{
                // marginBottom: "24px",
                display: "flex",
                flexDirection: "column-reverse",
              }}
            >
              <Col size={12}>
                <EachBlock
                  style={{ backgroundColor: backgroundColor }}
                  useCta={useCta}
                  shorter={shorter}
                >
                  <TitleBlock
                    isAutism={isAutism}
                    subTitleColor={subTitleColor}
                    paragraphColor={paragraphColor}
                    alignment={alignment}
                    moreMargin={moreMargin}
                    shorter={shorter}
                  >
                    {/* <Circle icon={icon} circleColor={circleColor} /> */}
                    <h3>{title}</h3>
                    <div dangerouslySetInnerHTML={{ __html: content }}></div>
                    {useCta && (
                      <BodyCallCtaWrapper>
                        <BodyCallCta
                          hasNumber={false}
                          backgroundColor={ctaBackground}
                          separatorColor={ctaSeparator}
                          text={ctaText}
                          phoneText={ctaPhoneText}
                          ctaPhoneLink={ctaPhoneLink}
                          component="TreatmentBlock"
                          website={website}
                        />
                      </BodyCallCtaWrapper>
                    )}
                  </TitleBlock>
                </EachBlock>
              </Col>
              <Col size={12}>
                <EachBlock
                  style={{
                    backgroundImage: `url(${image})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                  }}
                />
              </Col>
            </Row>
          </Container>
        </FullWidthContainer>
      )}
    </>
  );
};

export default Block;
